import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AccountUtils } from '../AccountUtils';
import type { ResourceMetricData } from '@/lib/types/Customer';

/** Aggregated monthly transfer data for chart/table display. */
export interface MonthlyTransferData {
  /** Interval start in epoch seconds. */
  start: number;
  /** Interval end in epoch seconds. */
  end: number;
  /** Total data transfer bytes for this month (summed across accounts). */
  transferBytes: number;
}

export interface UsageData {
  /** Storage used in bytes. */
  storageBytesUsed: number;
  /** Storage limit in bytes, or null if unlimited. */
  storageBytesLimit: number | null;
  /** Transfer used in bytes. */
  transferBytesUsed: number;
  /** Transfer limit in bytes, or null if unlimited. */
  transferBytesLimit: number | null;
  /** Monthly data transfer totals (aggregated from resourceMetrics). */
  monthlyTransfer: MonthlyTransferData[];
}

/**
 * Derives usage data from customerInfo (already cached via authKeys.customerInfo).
 * No separate API call needed — reads from shared cache.
 *
 * When master account is selected: shows aggregate usage.
 * When sub-account is selected: shows that sub-account's data only.
 */
export function useUsage() {
  const { customerInfo, selectedCustomer, isSubaccount } = useAuth();

  const usage: UsageData | null = useMemo(() => {
    if (!selectedCustomer || !customerInfo) return null;

    const limits = selectedCustomer.cfg.quota.limits;

    // Resource metrics for the selected account or aggregate
    const allMetrics = isSubaccount
      ? selectedCustomer.resourceMetrics
      : [
          ...customerInfo.customer.resourceMetrics,
          ...customerInfo.subcustomers.flatMap(s => s.resourceMetrics),
        ];

    const monthlyTransfer = aggregateDataTransfer(allMetrics);

    // Live storage/transfer come from the current-month resourceMetrics buckets;
    // the backend leaves limits.{storage,transfer}BytesUsed at 0. The *limit*
    // fields remain authoritative config.
    const now = Math.floor(Date.now() / 1000);
    return {
      storageBytesUsed: AccountUtils.currentMonthResourceUsage(allMetrics, 'storage', now),
      storageBytesLimit: limits.storageBytesLimit,
      transferBytesUsed: AccountUtils.currentMonthResourceUsage(allMetrics, 'datatransfer', now),
      transferBytesLimit: limits.transferBytesLimit,
      monthlyTransfer,
    };
  }, [selectedCustomer, customerInfo, isSubaccount]);

  return {
    usage,
    isLoading: !customerInfo,
  };
}

/**
 * Filter for 'datatransfer' monthly metrics, group by start timestamp,
 * and sum values across accounts. Matches legacy ResourceMetricsUtil.getDataTransferMetrics().
 */
function aggregateDataTransfer(metrics: ResourceMetricData[]): MonthlyTransferData[] {
  const bucketMap = new Map<number, MonthlyTransferData>();

  for (const m of metrics) {
    if (m.resourceMetricType !== 'datatransfer' || m.resourceMetricInterval !== 'month') continue;

    const existing = bucketMap.get(m.start);
    if (existing) {
      existing.transferBytes += m.value;
    } else {
      bucketMap.set(m.start, {
        start: m.start,
        end: m.end,
        transferBytes: m.value,
      });
    }
  }

  return Array.from(bucketMap.values()).sort((a, b) => a.start - b.start);
}
